import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18next from 'i18next';

interface PublishTask {
  id: string;
  progress: number; // 0-100
  status: 'uploading' | 'success' | 'error';
  message: string;
}

interface PublishPayload {
  userId: string;
  title: string;
  content: string | null;
  tags: string[];
  images: File[];
  categoryId: string | null;
  categoryIds?: string[];
  mediaType?: 'image' | 'video';
  videoFile?: File | null;
}

/** Extract the first frame of a video file as a JPEG Blob */
function extractVideoFirstFrame(videoFile: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(videoFile);
    video.src = url;

    video.onloadeddata = () => {
      video.currentTime = 0.1; // seek slightly to get a real frame
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('canvas context')); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (blob) resolve(blob);
            else reject(new Error('toBlob failed'));
          },
          'image/jpeg',
          0.85,
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('video load error'));
    };
  });
}

interface PublishContextValue {
  currentTask: PublishTask | null;
  publish: (payload: PublishPayload) => void;
  dismissTask: () => void;
}

const PublishContext = createContext<PublishContextValue | null>(null);

export const usePublish = () => {
  const ctx = useContext(PublishContext);
  if (!ctx) throw new Error('usePublish must be used within PublishProvider');
  return ctx;
};

const tp = (key: string, options?: Record<string, any>): string => i18next.t(`createPost.${key}`, options) as string;

export const PublishProvider = ({ children }: { children: ReactNode }) => {
  const [currentTask, setCurrentTask] = useState<PublishTask | null>(null);

  const dismissTask = useCallback(() => setCurrentTask(null), []);

  const publish = useCallback((payload: PublishPayload) => {
    const taskId = Date.now().toString();
    const { userId, title, content, tags, images, categoryId, categoryIds = [], mediaType = 'image', videoFile } = payload;

    setCurrentTask({ id: taskId, progress: 0, status: 'uploading', message: tp('preparing') });

    (async () => {
      try {
        let coverUrl: string | null = null;
        const allImageUrls: string[] = [];

        if (mediaType === 'video' && videoFile) {
          // ========== VIDEO FLOW ==========
          let videoDuration: number | null = null;
          try {
            videoDuration = await new Promise<number>((resolve) => {
              const video = document.createElement('video');
              video.preload = 'metadata';
              video.onloadedmetadata = () => {
                resolve(Math.round(video.duration));
                URL.revokeObjectURL(video.src);
              };
              video.onerror = () => resolve(0);
              video.src = URL.createObjectURL(videoFile);
            });
          } catch { videoDuration = null; }

          const totalSteps = 2 + images.length;
          let step = 0;

          // 1. Upload video
          const videoExt = videoFile.name.split('.').pop();
          const videoPath = `${userId}/${Date.now()}_video.${videoExt}`;

          setCurrentTask(prev => prev?.id === taskId ? {
            ...prev, progress: 5, message: tp('uploadingVideo'),
          } : prev);

          const { error: videoErr } = await supabase.storage.from('media').upload(videoPath, videoFile, {
            contentType: videoFile.type,
          });
          if (videoErr) throw videoErr;

          const { data: videoUrlData } = supabase.storage.from('media').getPublicUrl(videoPath);
          const videoUrl = videoUrlData.publicUrl;
          step++;

          // 2. Upload cover images if any
          const coverFiles: File[] = [...images];

          // If no cover image provided, extract first frame from video
          if (coverFiles.length === 0 && videoFile) {
            try {
              setCurrentTask(prev => prev?.id === taskId ? {
                ...prev, message: tp('generatingCover'),
              } : prev);
              const frameBlob = await extractVideoFirstFrame(videoFile);
              const frameFile = new File([frameBlob], `${Date.now()}_cover.jpg`, { type: 'image/jpeg' });
              coverFiles.push(frameFile);
            } catch {
              // silently skip – post will have no cover
            }
          }

          for (let i = 0; i < coverFiles.length; i++) {
            const file = coverFiles[i];
            const ext = file.name.split('.').pop();
            const path = `${userId}/${Date.now()}_${i}.${ext}`;

            setCurrentTask(prev => prev?.id === taskId ? {
              ...prev,
              progress: Math.round(((step + i) / totalSteps) * 90),
              message: tp('uploadingCover', { current: i + 1, total: coverFiles.length }),
            } : prev);

            const { error: uploadErr } = await supabase.storage.from('media').upload(path, file);
            if (uploadErr) throw uploadErr;

            const { data: urlData } = supabase.storage.from('media').getPublicUrl(path);
            allImageUrls.push(urlData.publicUrl);
            if (i === 0) coverUrl = urlData.publicUrl;
          }

          // 3. Insert post
          setCurrentTask(prev => prev?.id === taskId ? {
            ...prev, progress: 90, message: tp('posting'),
          } : prev);

          const { error } = await supabase.from('posts').insert({
            user_id: userId,
            title: title.trim(),
            content: content?.trim() || null,
            cover_image_url: coverUrl,
            image_urls: [videoUrl, ...allImageUrls],
            category_id: categoryId,
            category_ids: categoryIds,
            tags,
            media_type: 'video',
            video_duration: videoDuration || null,
          });

          if (error) throw error;

        } else {
          // ========== IMAGE FLOW ==========
          const totalSteps = images.length + 1;

          for (let i = 0; i < images.length; i++) {
            const file = images[i];
            const ext = file.name.split('.').pop();
            const path = `${userId}/${Date.now()}_${i}.${ext}`;

            setCurrentTask(prev => prev?.id === taskId ? {
              ...prev,
              progress: Math.round(((i) / totalSteps) * 100),
              message: tp('uploadingImage', { current: i + 1, total: images.length }),
            } : prev);

            const { error: uploadErr } = await supabase.storage.from('media').upload(path, file);
            if (uploadErr) throw uploadErr;

            const { data: urlData } = supabase.storage.from('media').getPublicUrl(path);
            allImageUrls.push(urlData.publicUrl);
            if (i === 0) coverUrl = urlData.publicUrl;
          }

          setCurrentTask(prev => prev?.id === taskId ? {
            ...prev,
            progress: Math.round((images.length / totalSteps) * 100),
            message: tp('posting'),
          } : prev);

          const { error } = await supabase.from('posts').insert({
            user_id: userId,
            title: title.trim(),
            content: content?.trim() || null,
            cover_image_url: coverUrl,
            image_urls: allImageUrls,
            category_id: categoryId,
            category_ids: categoryIds,
            tags,
          });

          if (error) throw error;
        }

        setCurrentTask(prev => prev?.id === taskId ? {
          ...prev, progress: 100, status: 'success', message: tp('publishSuccess'),
        } : prev);

        toast.success(tp('publishSuccess'));

        setTimeout(() => {
          setCurrentTask(prev => prev?.id === taskId ? null : prev);
        }, 2000);
      } catch (err: any) {
        setCurrentTask(prev => prev?.id === taskId ? {
          ...prev, status: 'error', message: err.message || tp('publishFailed'),
        } : prev);
        toast.error(tp('publishFailed') + ': ' + (err.message || i18next.t('common.unknownError')));
      }
    })();
  }, []);

  return (
    <PublishContext.Provider value={{ currentTask, publish, dismissTask }}>
      {children}
    </PublishContext.Provider>
  );
};
